import { useField } from 'formik';
import { Suspense } from 'react';
import { graphql } from 'react-relay';

import useFilterSearchPairsQuery from '../../../hooks/useFilterSearchPairsQuery';
import useLazyLoadQuery from '../../../hooks/useLazyLoadQuery';
import usePaginatorSearchOverlayQueryLoader from '../../../hooks/usePaginatorSearchOverlayQueryLoader';
import {
  InternalUserPaginatorSearchOverlayTokenField_internalUsersQuery,
  InternalUserPaginatorSearchOverlayTokenField_internalUsersQuery$variables,
} from '../../../relay/__generated__/InternalUserPaginatorSearchOverlayTokenField_internalUsersQuery.graphql';
import {
  InternalUserPaginatorSearchOverlayTokenField_UserValue_internalUserQuery,
} from '../../../relay/__generated__/InternalUserPaginatorSearchOverlayTokenField_UserValue_internalUserQuery.graphql';
import { isNullable } from '../../../utils/is';
import Checkbox from '../../core/Checkbox';
import EmptyState from '../../core/EmptyState';
import Grid from '../../core/Grid';
import ItemList from '../../core/ItemList';
import PreloadedQueryRenderer from '../../core/PreloadedQueryRenderer';
import ScrollContainer from '../../core/ScrollContainer';
import SearchOverlayTokenField, { SearchOverlayTokenFieldProps } from '../../core/SearchOverlayTokenField';
import Spinner from '../../core/Spinner';
import Text from '../../core/Text';
import View from '../../core/View';
import InternalUserPaginator from '../InternalUserPaginator';

const internalUsersForInternalUserPaginatorSearchOverlayTokenField = graphql`
  query InternalUserPaginatorSearchOverlayTokenField_internalUsersQuery(
    $filters: MATInternalUserFilter
    $order: MATInternalUserOrder
    $first: Int
    $after: String
  ) {
    internalUsers(filters: $filters, order: $order, first: $first, after: $after) {
      edges {
        node {
          id
          userName
          loginId
          userSequence
        }
      }
    }
    ...InternalUserPaginator_query @arguments(filters: $filters, order: $order, first: $first, after: $after)
  }
`;

type Props = {
  variables?: InternalUserPaginatorSearchOverlayTokenField_internalUsersQuery$variables;
} & Omit<SearchOverlayTokenFieldProps, 'renderItemList'>;

const InternalUserPaginatorSearchOverlayTokenField = ({ name = '', value: propValue, variables, placeholder, ...props }: Props) => {
  const [{ value: baseValue }] = useField<string[] | undefined>({ name });
  const value = !isNullable(propValue) ? propValue : baseValue;
  const filterSearchPairs = useFilterSearchPairsQuery('MATInternalUserFilter');

  const [searchOverlayFieldProps, queryReference, , , isPending] =
    usePaginatorSearchOverlayQueryLoader<InternalUserPaginatorSearchOverlayTokenField_internalUsersQuery>(
      internalUsersForInternalUserPaginatorSearchOverlayTokenField,
      variables,
    );

  return (
    <SearchOverlayTokenField
      name={name}
      value={value}
      placeholder={placeholder ?? filterSearchPairs.map(({ value }) => value).join(', ')}
      renderTokenText={(id) =>
        (
          <Suspense>
            <InternalUserValue id={id} />
          </Suspense>
        ) as any
      }
      renderItemList={({ containerRef, handleSelect }, itemProps) =>
        queryReference ? (
          <Suspense fallback={<Spinner sx={{ paddingY: 3 }} />}>
            <PreloadedQueryRenderer
              query={internalUsersForInternalUserPaginatorSearchOverlayTokenField}
              queryReference={queryReference}
            >
              {(queryData) => (
                <InternalUserPaginator fragmentReference={queryData}>
                  {(data, { hasNext, isLoadingNext, loadMore }) => (
                    <ScrollContainer
                      ref={containerRef as React.RefObject<HTMLDivElement>}
                      onScrollReachEnd={() => {
                        if (hasNext && !isLoadingNext) loadMore(20);
                      }}
                    >
                      {isPending ? (
                        <Spinner sx={{ paddingY: 3 }} />
                      ) : (
                        <ItemList
                          items={data.internalUsers.edges}
                          renderItem={({ node }) => (
                            <View sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Text sx={{ fontSize: 0, color: 'fg.muted' }}>{node.userSequence}</Text>
                              <Text sx={{ fontWeight: 'bold' }}>{node.userName}</Text>
                              <Text sx={{ fontSize: 0, color: 'fg.muted' }}>({node.loginId})</Text>
                            </View>
                          )}
                          renderItemWrapper={(children, { node: { id } }) => (
                            <View
                              {...itemProps}
                              key={id}
                              onClick={(e) => handleSelect(e, id)}
                              onKeyDown={(e) => handleSelect(e, id)}
                            >
                              <Grid gapX={2} wrap={false}>
                                <Grid.Unit size={'min'}>
                                  <Checkbox checked={value?.some((selectedId) => selectedId === id)} />
                                </Grid.Unit>
                                <Grid.Unit size={'max'}>{children}</Grid.Unit>
                              </Grid>
                            </View>
                          )}
                          emptyState={
                            <View sx={{ paddingY: 3 }}>
                              <EmptyState title={'검색 결과가 없어요'} description={'다른 검색어로 다시 시도해주세요.'} />
                            </View>
                          }
                        />
                      )}
                      <View>{isLoadingNext ? <Spinner size={'small'} /> : null}</View>
                    </ScrollContainer>
                  )}
                </InternalUserPaginator>
              )}
            </PreloadedQueryRenderer>
          </Suspense>
        ) : null
      }
      {...searchOverlayFieldProps}
      {...props}
    />
  );
};

const InternalUserValue = ({ id }: { id: string }) => {
  const [{ internalUser }] = useLazyLoadQuery<InternalUserPaginatorSearchOverlayTokenField_UserValue_internalUserQuery>(
    graphql`
      query InternalUserPaginatorSearchOverlayTokenField_UserValue_internalUserQuery($id: ID!) {
        internalUser(id: $id) {
          id
          userName
          loginId
          userSequence
        }
      }
    `,
    { id },
  );

  return `${internalUser.userSequence} ${internalUser.userName} (${internalUser.loginId})` as unknown as JSX.Element;
};

export default InternalUserPaginatorSearchOverlayTokenField;
